/** Host / narrator photo overlays — timeline clips with draggable on-frame placement. */

import { mediaUrlForPlayback, persistedMediaUrl } from '@/lib/mediaUrls';
import { resolveBackgroundTemplate } from '@/lib/videoEditorBackgroundTemplates';
import { normalizeRotationSigned } from '@/lib/subtitles/subtitleTransform';

export const DEFAULT_HOST_OVERLAY_DURATION = 8;

/** Host photos can be smaller than subtitle caption boxes. */
export const HOST_OVERLAY_MIN_LAYER = {
  width: 0.06,
  height: 0.06,
};

export const OVERLAY_KIND_HOST = 'host';
export const OVERLAY_KIND_BACKGROUND = 'background';
export const OVERLAY_KIND_CARD = 'card';

export function normalizeOverlayKind(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === OVERLAY_KIND_BACKGROUND) return OVERLAY_KIND_BACKGROUND;
  if (raw === OVERLAY_KIND_CARD || raw === 'carousel' || raw === 'carousel_card') return OVERLAY_KIND_CARD;
  return OVERLAY_KIND_HOST;
}

export function isTitleOverlayClip(clip) {
  if (!clip) return false;
  const lane = String(clip.reelsLane || clip.reels_lane || '').toLowerCase();
  const type = String(clip.brollType || clip.broll_type || '').toLowerCase();
  return lane === 'title' || type === 'title_overlay';
}

export function isArtifactStickerClip(clip) {
  if (!clip) return false;
  if (normalizeOverlayKind(clip.overlayKind ?? clip.overlay_kind) === OVERLAY_KIND_CARD) return false;
  if (clip.sticker) return true;
  const lane = String(clip.reelsLane || clip.reels_lane || '').toLowerCase();
  const type = String(clip.brollType || clip.broll_type || '').toLowerCase();
  return lane === 'artifact' || type === 'context_overlay';
}

export function isLayoutBrollClip(clip) {
  return String(clip?.reelsLane || clip?.reels_lane || '').toLowerCase() === 'broll';
}

export function isBackgroundOverlayClip(clip) {
  return normalizeOverlayKind(clip?.overlayKind ?? clip?.overlay_kind) === OVERLAY_KIND_BACKGROUND;
}

/** Full-frame layer behind main video (normalized center coords). */
export function fullFrameBackgroundLayer(aspectRatio = '16:9') {
  return normalizeHostOverlayLayer({
    x: 0.5,
    y: 0.5,
    width: 1,
    height: 1,
    rotation: 0,
    textAlign: 'center',
  }, aspectRatio);
}

function clampHostDim(value, min, max, fallback) {
  const v = Number(value);
  return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
}

function clampHostCenter(value, fallback) {
  const v = Number(value);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;
}

export function normalizeHostOverlayLayer(raw, aspectRatio = '16:9', bounds = null) {
  void aspectRatio;
  const defaults = {
    x: 0.88,
    y: 0.88,
    width: 0.18,
    height: 0.18,
    rotation: 0,
    textAlign: 'center',
  };
  if (!raw || typeof raw !== 'object') return { ...defaults };

  const allowFull = Boolean(bounds?.maxWidth >= 1 || bounds?.maxHeight >= 1);
  const maxWidth = Number(bounds?.maxWidth) > 0
    ? Number(bounds.maxWidth)
    : (Number(raw.width) >= 0.99 || allowFull ? 1 : 0.95);
  const maxHeight = Number(bounds?.maxHeight) > 0
    ? Number(bounds.maxHeight)
    : (Number(raw.height) >= 0.99 || allowFull ? 1 : 0.95);
  const minWidth = Number(bounds?.minWidth) > 0 ? Number(bounds.minWidth) : HOST_OVERLAY_MIN_LAYER.width;
  const minHeight = Number(bounds?.minHeight) > 0 ? Number(bounds.minHeight) : HOST_OVERLAY_MIN_LAYER.height;

  return {
    x: clampHostCenter(raw.x, defaults.x),
    y: clampHostCenter(raw.y, defaults.y),
    width: clampHostDim(raw.width, minWidth, maxWidth, defaults.width),
    height: clampHostDim(raw.height, minHeight, maxHeight, defaults.height),
    rotation: normalizeRotationSigned(raw.rotation ?? 0),
    textAlign: 'center',
  };
}

/** Default bottom-right host photo box (normalized center coords). */
export function defaultHostOverlayLayer(aspectRatio = '16:9') {
  const size = 0.18;
  return normalizeHostOverlayLayer({
    x: 0.88,
    y: 0.88,
    width: size,
    height: size,
    rotation: 0,
    textAlign: 'center',
  }, aspectRatio);
}

export const HOST_OVERLAY_LAYER_RESIZE_OPTIONS = {
  minWidth: HOST_OVERLAY_MIN_LAYER.width,
  minHeight: HOST_OVERLAY_MIN_LAYER.height,
  maxWidth: 0.95,
  maxHeight: 0.95,
  /** Allow center anywhere on frame; export clamps pixels to stay on-screen. */
  positionMode: 'free',
};

export const LAYOUT_BROLL_LAYER_RESIZE_OPTIONS = {
  minWidth: 0.12,
  minHeight: 0.12,
  maxWidth: 1,
  maxHeight: 1,
  positionMode: 'free',
};

export function coerceHostOverlayTracks(tracks) {
  if (!Array.isArray(tracks)) return [[]];
  const lanes = tracks.map((lane) =>
    (Array.isArray(lane) ? lane.filter((c) => c && typeof c === 'object') : []),
  );
  return lanes.length ? lanes : [[]];
}

export function hostOverlayRefsFromUploadUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return { imageUrl: '', image_s3_url: '' };
  if (raw.includes('amazonaws.com')) {
    return { imageUrl: raw, image_s3_url: raw };
  }
  const persisted = persistedMediaUrl(raw) || raw;
  return {
    imageUrl: persisted,
    ...(raw.includes('amazonaws.com') ? { image_s3_url: raw } : {}),
  };
}

export function resolveHostOverlayClipForPlayback(clip, aspectRatio = '16:9') {
  const normalized = normalizeHostOverlayClip(clip, aspectRatio);
  const playback = hostOverlayPlaybackUrl(normalized);
  if (!playback) return normalized;
  const s3 = String(normalized.image_s3_url || '').trim();
  return {
    ...normalized,
    imageUrl: playback,
    ...(s3 || playback.includes('amazonaws.com')
      ? { image_s3_url: s3 || playback }
      : {}),
  };
}

export function loadHostOverlayTracksFromEditor(editor, aspectRatio = '16:9') {
  if (editor?.overlayTracks) {
    return coerceHostOverlayTracks(JSON.parse(JSON.stringify(editor.overlayTracks)))
      .map((lane) => lane.map((clip) => resolveHostOverlayClipForPlayback(clip, aspectRatio)));
  }
  return [[]];
}

/** Default 1. Legacy unset/broken values were stored as 0 — treat those as opaque. */
export function normalizeHostOverlayOpacity(value) {
  if (value == null || value === '') return 1;
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0) return 1;
  return Math.min(1, Math.max(0.05, v));
}

export function normalizeHostOverlayClip(raw, aspectRatio = '16:9') {
  const src = raw && typeof raw === 'object' ? raw : {};
  const dur = Math.max(0.5, Number(src.duration) || DEFAULT_HOST_OVERLAY_DURATION);
  const imageUrl = String(src.imageUrl || src.image_url || '').trim();
  const imageS3 = String(src.image_s3_url || src.imageS3Url || '').trim();
  return {
    id: String(src.id || `host-overlay-${Date.now()}`),
    name: String(src.name || 'Host photo').trim() || 'Host photo',
    imageUrl,
    ...(imageS3 ? { image_s3_url: imageS3 } : {}),
    ...(src.templateId || src.template_id
      ? { templateId: String(src.templateId || src.template_id) }
      : {}),
    overlayKind: normalizeOverlayKind(src.overlayKind ?? src.overlay_kind),
    startTime: Math.max(0, Number(src.startTime ?? src.start_time) || 0),
    duration: dur,
    layer: src.layer
      ? normalizeHostOverlayLayer(src.layer, aspectRatio)
      : (normalizeOverlayKind(src.overlayKind ?? src.overlay_kind) === OVERLAY_KIND_BACKGROUND
        ? fullFrameBackgroundLayer(aspectRatio)
        : defaultHostOverlayLayer(aspectRatio)),
    opacity: normalizeHostOverlayOpacity(src.opacity),
    borderRadius: Math.max(
      0,
      Math.min(0.5, Number(src.borderRadius) ?? (normalizeOverlayKind(src.overlayKind ?? src.overlay_kind) === OVERLAY_KIND_BACKGROUND ? 0 : 0.12)),
    ),
    muted: Boolean(src.muted),
    ...(src.carouselGroupId ? { carouselGroupId: String(src.carouselGroupId) } : {}),
    ...(src.carouselIndex != null ? { carouselIndex: Number(src.carouselIndex) || 0 } : {}),
    ...(src.carouselParams ? { carouselParams: src.carouselParams } : {}),
    ...(src.cardTitle != null ? { cardTitle: String(src.cardTitle) } : {}),
    ...(src.cardNumber != null ? { cardNumber: String(src.cardNumber) } : {}),
    ...(src.cardHighlight != null ? { cardHighlight: String(src.cardHighlight) } : {}),
    ...(src.reelsLane || src.reels_lane ? { reelsLane: String(src.reelsLane || src.reels_lane) } : {}),
    ...(src.brollType || src.broll_type ? { brollType: String(src.brollType || src.broll_type) } : {}),
    ...(src.triggerText || src.trigger_text ? { triggerText: String(src.triggerText || src.trigger_text) } : {}),
    ...(src.sticker ? { sticker: true } : {}),
    ...(src.titleLine1 != null ? { titleLine1: String(src.titleLine1) } : {}),
    ...(src.titleLine2 != null ? { titleLine2: String(src.titleLine2) } : {}),
    ...(src.titleTemplateId || src.title_template_id
      ? { titleTemplateId: String(src.titleTemplateId || src.title_template_id) }
      : {}),
    ...(src.titleStyle || src.title_style ? { titleStyle: src.titleStyle || src.title_style } : {}),
    ...(src.suggestionId || src.suggestion_id ? { suggestionId: String(src.suggestionId || src.suggestion_id) } : {}),
    ...(src.displayText != null || src.display_text != null
      ? { displayText: String(src.displayText ?? src.display_text ?? '') }
      : {}),
    ...(src.generationPrompt || src.generation_prompt
      ? { generationPrompt: String(src.generationPrompt || src.generation_prompt) }
      : {}),
    ...(src.sceneId || src.scene_id ? { sceneId: String(src.sceneId || src.scene_id) } : {}),
  };
}

export function hostOverlayClipEndTime(clip) {
  return (Number(clip?.startTime) || 0) + Math.max(0.01, Number(clip?.duration) || 0.01);
}

export function hostOverlayPlaybackUrl(clip) {
  if (!clip) return '';
  const raw = String(clip.imageUrl || '').trim();
  const s3 = String(clip.image_s3_url || clip.imageS3Url || '').trim();
  return mediaUrlForPlayback(raw || s3, { s3Url: s3 || undefined });
}

export function hostOverlayClipHasMedia(clip) {
  if (normalizeOverlayKind(clip?.overlayKind ?? clip?.overlay_kind) === OVERLAY_KIND_CARD) {
    return Boolean(hostOverlayPlaybackUrl(clip) || clip?.cardTitle || clip?.name);
  }
  if (isTitleOverlayClip(clip)) return true;
  if (isArtifactStickerClip(clip)) return true;
  if (isLayoutBrollClip(clip)) return true;
  return Boolean(hostOverlayPlaybackUrl(clip));
}

export function hostOverlaysActiveAtTime(tracks, timeSec, { includeMuted = false } = {}) {
  const t = Number(timeSec) || 0;
  return (tracks || []).flat().filter((clip) => {
    if (!hostOverlayClipHasMedia(clip)) return false;
    if (!includeMuted && clip.muted) return false;
    const start = Number(clip.startTime) || 0;
    const end = hostOverlayClipEndTime(clip);
    return t >= start && t <= end + 0.05;
  });
}

/** Nearest host clip to playhead (in-range first, else closest edge). */
export function hostOverlayClipNearestToTime(tracks, timeSec, { includeMuted = false } = {}) {
  const t = Number(timeSec) || 0;
  const flat = (tracks || []).flat().filter((clip) => {
    if (!hostOverlayClipHasMedia(clip)) return false;
    if (!includeMuted && clip.muted) return false;
    return true;
  });
  if (!flat.length) return null;
  let nearest = flat[0];
  let nearestDist = Infinity;
  flat.forEach((clip) => {
    const start = Number(clip.startTime) || 0;
    const end = hostOverlayClipEndTime(clip);
    if (t >= start && t <= end + 0.05) {
      nearest = clip;
      nearestDist = -1;
      return;
    }
    const dist = t < start ? start - t : t - end;
    if (nearestDist < 0) return;
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = clip;
    }
  });
  return nearest;
}

/**
 * Clips to render in the preview player.
 * Always respects timeline ranges — never show a host/logo over intro/outro
 * bookends or gaps where the clip is not placed.
 */
export function hostOverlaysForPreview(tracks, timeSec, {
  isPlaying = false,
  selectedClipId = null,
  trackActive = true,
  /** When true (intro/outro bookend), never show host overlays. */
  hideOverlays = false,
} = {}) {
  if (!trackActive || hideOverlays) return [];
  const atPlayhead = hostOverlaysActiveAtTime(tracks, timeSec);
  if (isPlaying) return atPlayhead;
  if (atPlayhead.length) return atPlayhead;

  // Paused: allow editing the selected clip only while the playhead is on it.
  if (selectedClipId) {
    const selected = atPlayhead.find((c) => c.id === selectedClipId);
    if (selected) return [selected];
    const flat = (tracks || []).flat();
    const sel = flat.find((c) => c.id === selectedClipId);
    if (sel && hostOverlayClipHasMedia(sel) && !sel.muted) {
      const start = Number(sel.startTime) || 0;
      const end = hostOverlayClipEndTime(sel);
      const t = Number(timeSec) || 0;
      if (t >= start && t <= end + 0.05) return [sel];
    }
  }
  return [];
}

export function createHostOverlayClip({
  playhead = 0,
  duration = DEFAULT_HOST_OVERLAY_DURATION,
  aspectRatio = '16:9',
  imageUrl = '',
  name = 'Host photo',
} = {}) {
  const start = Math.max(0, Number(playhead) || 0);
  const refs = hostOverlayRefsFromUploadUrl(imageUrl);
  return normalizeHostOverlayClip({
    id: `host-overlay-${Date.now()}`,
    name,
    ...refs,
    overlayKind: OVERLAY_KIND_HOST,
    startTime: start,
    duration: Math.max(0.5, Number(duration) || DEFAULT_HOST_OVERLAY_DURATION),
    layer: defaultHostOverlayLayer(aspectRatio),
  }, aspectRatio);
}

export function createBackgroundTemplateClip({
  templateId,
  playhead = 0,
  duration = DEFAULT_HOST_OVERLAY_DURATION,
  aspectRatio = '16:9',
} = {}) {
  const template = resolveBackgroundTemplate(templateId);
  if (!template) {
    throw new Error(`Unknown background template: ${templateId}`);
  }
  const start = Math.max(0, Number(playhead) || 0);
  return normalizeHostOverlayClip({
    id: `bg-template-${template.id}`,
    name: template.name,
    imageUrl: template.imageUrl,
    templateId: template.id,
    overlayKind: OVERLAY_KIND_BACKGROUND,
    startTime: start,
    duration: Math.max(0.5, Number(duration) || DEFAULT_HOST_OVERLAY_DURATION),
    layer: fullFrameBackgroundLayer(aspectRatio),
    borderRadius: 0,
  }, aspectRatio);
}

export function layerToOverlayBox(layer, vw, vh) {
  const ly = layer && typeof layer === 'object' ? layer : {};
  const x = Number(ly.x ?? 0.88);
  const y = Number(ly.y ?? 0.88);
  const w = Number(ly.width ?? 0.16);
  const h = Number(ly.height ?? 0.16);
  const ow = Math.max(2, Math.round(w * vw));
  const oh = Math.max(2, Math.round(h * vh));
  let ox = Math.round((x - w / 2) * vw);
  let oy = Math.round((y - h / 2) * vh);
  ox = Math.max(0, Math.min(Math.max(0, vw - ow), ox));
  oy = Math.max(0, Math.min(Math.max(0, vh - oh), oy));
  return { ox, oy, ow, oh };
}

/** Server export payload; shifts start times when intro bookend is present. */
export function buildHostOverlayExportPayload(overlayTracks, {
  aspectRatio = '16:9',
  introDuration = 0,
} = {}) {
  const intro = Math.max(0, Number(introDuration) || 0);
  const filter = (clip) => {
    const url = hostOverlayPlaybackUrl(clip);
    return Boolean(url && !url.startsWith('blob:'));
  };
  return (overlayTracks || []).flat()
    .filter((clip) => !clip.muted)
    .map((clip) => normalizeHostOverlayClip(clip, aspectRatio))
    .filter((clip) => (
      filter(clip)
      || (clip.overlayKind === OVERLAY_KIND_CARD && (clip.cardTitle || clip.name))
      || isArtifactStickerClip(clip)
    ))
    .map((clip) => {
      const url = hostOverlayPlaybackUrl(clip);
      const layer = clip.layer || defaultHostOverlayLayer(aspectRatio);
      const sticker = isArtifactStickerClip(clip);
      return {
        image_url: url,
        start_time: (Number(clip.startTime) || 0) + intro,
        playback_seconds: Math.max(0.01, Number(clip.duration) || DEFAULT_HOST_OVERLAY_DURATION),
        opacity: clip.opacity,
        border_radius: sticker ? 0 : clip.borderRadius,
        layer: {
          x: layer.x,
          y: layer.y,
          width: layer.width,
          height: layer.height,
          rotation: layer.rotation ?? 0,
        },
        overlay_kind: clip.overlayKind || OVERLAY_KIND_HOST,
        animation_in: clip.carouselParams?.animationIn || '',
        animation_duration: Number(clip.carouselParams?.animationDuration) || 0,
        card_title: clip.cardTitle || '',
        card_number: clip.cardNumber || '',
        card_highlight: clip.cardHighlight || '',
        sticker,
        reels_lane: clip.reelsLane || '',
        broll_type: clip.brollType || '',
        trigger_text: clip.triggerText || '',
      };
    });
}
